import { AdvancedType, BasicType } from '@core/constants';
import { IBlock } from '@core/typings';
// Straight from the leaf module, not the block.ts re-export — this file is
// pulled in by every block's render and must not drag BlockManager in with it.
import { isDynamicListingColumn } from './listingScope';

export function getPlaceholder(params: Parameters<IBlock['render']>[0]) {
  const {
    data: { type },
    mode,
    dataSource,
  } = params;

  // Canvas and raster show the drop hint — they are views of the design being
  // built, and an unfinished layout should look unfinished. The sendable HTML
  // never does. Neither does ClientBAY's Preview & Test, which promises exactly
  // what the recipient receives; Design Manager's preview keeps it, because
  // there the empty state is the point.
  //
  // isDesignAdmin is the platform discriminator, not a role check: Design
  // Manager runs as design admin, ClientBAY passes false. previewMode alone
  // cannot separate them (both previews set it) and rasterMode has to be
  // exempted because the raster runs under previewMode too.
  if (mode === 'production') return null;
  if (dataSource?.previewMode && !dataSource?.rasterMode && !dataSource?.isDesignAdmin) {
    return null;
  }
  const attrs = params.data.attributes ?? {};
  const hasBackgroundImage = !!attrs['background-url'];

  let text: null | string = null;
  let padding: string = '0';
  if (type === BasicType.PAGE) {
    text = 'Drop a Wrapper block here';
  } else if (
    type === BasicType.WRAPPER ||
    type === AdvancedType.WRAPPER ||
    // The Listing Wrapper IS a wrapper — same empty state, only the copy
    // differs, so it picks up the wrapper's padding too.
    type === 'custom_listing_block'
  ) {
    text =
      type === 'custom_listing_block'
        ? 'Drag & drop content blocks here. Everything placed inside this container will share data from one single listing.'
        : 'Drag & drop content blocks here.';
    padding = '5rem 0';
  } else if (isDynamicListingColumn(params.data)) {
    // Only the listing column — an ordinary column keeps the generic copy.
    text =
      'Drag & drop content blocks here. Everything placed inside this column will share data from one single listing.';
  } else if (
    type === BasicType.SECTION ||
    type === BasicType.GROUP ||
    type === AdvancedType.SECTION ||
    type === AdvancedType.GROUP ||
    type === BasicType.COLUMN ||
    type === AdvancedType.COLUMN
  ) {
    text = 'Drag & drop content blocks here.';
  }

  if (!text) return null;

  // SVG <text> does not wrap: anything wider than the 300-unit viewBox is
  // simply clipped at both ends. Break the copy into lines that fit, and give
  // the box room for however many we end up with.
  const MAX_CHARS_PER_LINE = 42;
  const LINE_HEIGHT = 18;

  const lines = text.split(' ').reduce<string[]>((acc, word) => {
    const current = acc[acc.length - 1];
    if (current && `${current} ${word}`.length <= MAX_CHARS_PER_LINE) {
      acc[acc.length - 1] = `${current} ${word}`;
    } else {
      acc.push(word);
    }
    return acc;
  }, []);

  const textSpans = lines
    .map(
      (line, index) =>
        `<tspan x="150" dy="${index === 0 ? 0 : LINE_HEIGHT}">${line}</tspan>`
    )
    .join('');
  const viewBoxHeight = 110 + Math.max(0, lines.length - 1) * LINE_HEIGHT;

  return `
   <mj-text color="#666">
    <div style="text-align: center; padding: ${padding}">
      <div>
        <svg width="300" fill="currentColor" style="max-width: 100%;" viewBox="0 -24 300 ${viewBoxHeight}">
          <circle cx="150" cy="14" r="35" fill="${hasBackgroundImage ? 'transparent' : '#d1d2d4'}" stroke="none"/>
          <g transform="translate(136, 0) scale(1.75)">
            <path d="M2.5 0q-.25 0-.487.048l.194.98A1.5 1.5 0 0 1 2.5 1h.458V0zm2.292 0h-.917v1h.917zm1.833 0h-.917v1h.917zm1.833 0h-.916v1h.916zm1.834 0h-.917v1h.917zm1.833 0h-.917v1h.917zM13.5 0h-.458v1h.458q.151 0 .293.029l.194-.981A2.5 2.5 0 0 0 13.5 0m2.079 1.11a2.5 2.5 0 0 0-.69-.689l-.556.831q.248.167.415.415l.83-.556zM1.11.421a2.5 2.5 0 0 0-.689.69l.831.556c.11-.164.251-.305.415-.415zM16 2.5q0-.25-.048-.487l-.98.194q.027.141.028.293v.458h1zM.048 2.013A2.5 2.5 0 0 0 0 2.5v.458h1V2.5q0-.151.029-.293zM0 3.875v.917h1v-.917zm16 .917v-.917h-1v.917zM0 5.708v.917h1v-.917zm16 .917v-.917h-1v.917zM0 7.542v.916h1v-.916zm15 .916h1v-.916h-1zM0 9.375v.917h1v-.917zm16 .917v-.917h-1v.917zm-16 .916v.917h1v-.917zm16 .917v-.917h-1v.917zm-16 .917v.458q0 .25.048.487l.98-.194A1.5 1.5 0 0 1 1 13.5v-.458zm16 .458v-.458h-1v.458q0 .151-.029.293l.981.194Q16 13.75 16 13.5M.421 14.89c.183.272.417.506.69.689l.556-.831a1.5 1.5 0 0 1-.415-.415zm14.469.689c.272-.183.506-.417.689-.69l-.831-.556c-.11.164-.251.305-.415.415l.556.83zm-12.877.373Q2.25 16 2.5 16h.458v-1H2.5q-.151 0-.293-.029zM13.5 16q.25 0 .487-.048l-.194-.98A1.5 1.5 0 0 1 13.5 15h-.458v1zm-9.625 0h.917v-1h-.917zm1.833 0h.917v-1h-.917zm1.834-1v1h.916v-1zm1.833 1h.917v-1h-.917zm1.833 0h.917v-1h-.917zM8.5 4.5a.5.5 0 0 0-1 0v3h-3a.5.5 0 0 0 0 1h3v3a.5.5 0 0 0 1 0v-3h3a.5.5 0 0 0 0-1h-3z"/>
          </g>
          <text x="150" y="78" text-anchor="middle" font-size="14">${textSpans}</text>
        </svg>
      </div>
    </div>
   </mj-text>
  `;
}
